// fetch wrapper: JWT header, problem+json mapping, refresh rotation on 401. 04-web §5.
import { API_BASE } from "./config.js";
import { clearSession, loadSession, saveSession } from "./tokens.js";
import type { Session } from "../models.js";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, detail?: string) {
    super(detail ?? code);
    this.status = status;
    this.code = code;
  }
}

interface ProblemJson {
  status?: number;
  code?: string;
  detail?: string;
}

// Single-flight: concurrent 401s share one refresh so rotated refresh tokens
// aren't double-spent (which would make the 2nd call fail and wipe the session).
let refreshing: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const s = loadSession();
  if (!s) return false;
  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: s.refresh_token }),
    });
  } catch {
    // Network error — the 401 may be transient; keep the session.
    return false;
  }
  // Only a definitive auth rejection (refresh token actually invalid) clears the
  // session. Transient server errors (5xx/429) must not wipe a valid session.
  if (resp.status === 401 || resp.status === 403) {
    clearSession();
    return false;
  }
  if (!resp.ok) return false;
  try {
    saveSession((await resp.json()) as Session);
  } catch {
    return false;
  }
  return true;
}

function refresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const s = loadSession();
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (s) headers["authorization"] = `Bearer ${s.access_token}`;

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 401 && retry && (await refresh())) {
    return request<T>(method, path, body, false);
  }
  if (!resp.ok) {
    let problem: ProblemJson = {};
    try {
      problem = (await resp.json()) as ProblemJson;
    } catch {
      /* non-json error */
    }
    throw new ApiError(resp.status, problem.code ?? "ERROR", problem.detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
